import React, { useState, useEffect } from 'react';
import { getToken } from '../utils/storage';
import CustomerLayout from '../components/CustomerLayout';
import CollapsibleTable from '../components/CollapsibleTable';
import axios from 'axios';
import { Plus, Trash2, Edit, Mail, Clock, X } from 'lucide-react';

import toast from '../utils/toast';
import confirm from '../utils/confirm';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to format date without seconds
const formatDateTime = (isoString) => {
  if (!isoString) return '-';
  const date = new Date(isoString);
  return date.toLocaleString('no-NO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Login History Popup Component
const LoginHistoryPopup = ({ user, onClose }) => {
  const { t } = useTranslation();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const token = getToken();
        const response = await axios.get(`${API}/users/${user.id}/login-history`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setHistory(response.data);
      } catch (error) {
        console.error('Could not load login history:', error);
        toast.error('Kunne ikke laste innloggingshistorikk');
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [user.id]);

  return (
    <>
      <div 
        className="modal-overlay" 
        onClick={onClose}
        style={{ zIndex: 1000 }}
      />
      <div 
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: '#1a1a1a',
          borderRadius: '12px',
          padding: '1.5rem',
          width: '90%',
          maxWidth: '500px',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1001,
          border: '1px solid rgba(80, 227, 194, 0.3)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
        }}
      >
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '1rem',
          paddingBottom: '1rem',
          borderBottom: '1px solid rgba(255,255,255,0.1)'
        }}>
          <div>
            <h3 style={{ margin: 0, color: '#50E3C2', fontSize: '1.1rem' }}>
              <Clock size={18} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
              Innloggingshistorikk
            </h3>
            <p style={{ margin: '0.25rem 0 0 0', color: '#aaa', fontSize: '0.9rem' }}>
              {user.email}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#aaa',
              cursor: 'pointer',
              padding: '0.5rem'
            }}
          >
            <X size={24} />
          </button>
        </div>

        <div style={{ 
          flex: 1, 
          overflowY: 'auto',
          minHeight: '200px'
        }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#aaa' }}>
              Laster...
            </div>
          ) : history.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#aaa' }}>
              Ingen innlogginger registrert
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {history.map((entry, index) => (
                <div 
                  key={index}
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    padding: '0.75rem 1rem',
                    borderRadius: '8px',
                    borderLeft: index === 0 ? '3px solid #50E3C2' : '3px solid transparent'
                  }}
                >
                  <div style={{ 
                    color: index === 0 ? '#50E3C2' : '#fff',
                    fontWeight: index === 0 ? '600' : '400',
                    fontSize: '0.95rem'
                  }}>
                    {formatDateTime(entry.login_at)}
                    {index === 0 && (
                      <span style={{ 
                        marginLeft: '0.5rem', 
                        fontSize: '0.75rem', 
                        background: '#50E3C2', 
                        color: '#000',
                        padding: '0.15rem 0.5rem',
                        borderRadius: '4px'
                      }}>
                        Siste
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const CustomerUsers = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', preferred_language: '' });
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [showLoginHistory, setShowLoginHistory] = useState(null);

  useEffect(() => {
    fetchUsers();
    fetchLanguages();
  }, []);

  const fetchLanguages = async () => {
    try {
      const response = await axios.get(`${API}/languages`);
      setAvailableLanguages(response.data);
    } catch (error) {
      console.error('Could not load languages:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = getToken();
      // Use new endpoint that includes last_login
      const response = await axios.get(`${API}/users-with-login`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      toast.error(t('customerUsers.errorLoadingUsers'));
    }
  };

  const handleCreateUser = async () => {
    try {
      const token = getToken();
      if (editingUser) {
        // Update existing user
        const updateData = { 
          name: formData.name, 
          email: formData.email,
          // Send empty string to clear, or the actual language code
          preferred_language: formData.preferred_language
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await axios.put(`${API}/users/${editingUser.id}`, updateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(t('customerUsers.userUpdated'));
      } else {
        // Create new user
        await axios.post(`${API}/users`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(t('customerUsers.userCreated'));
      }
      setShowModal(false);
      setEditingUser(null);
      setFormData({ name: '', email: '', password: '', preferred_language: '' });
      fetchUsers();
    } catch (error) {
      // Check if this is a demo user restriction error
      if (error.response?.status === 403 && error.response?.data?.detail?.includes('demo-konto')) {
        toast.error(error.response.data.detail, { duration: 8000 });
        setShowModal(false);
      } else {
        toast.error(editingUser ? t('customerUsers.errorUpdatingUser') : t('customerUsers.errorCreatingUser'));
      }
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setFormData({ 
      name: user.name, 
      email: user.email, 
      password: '',
      preferred_language: user.preferred_language || ''
    });
    setShowModal(true);
  };

  const handleResetPassword = async (user) => {
    if (!await confirm.info(t('customerUsers.confirmPasswordReset', { email: user.email }))) {
      return;
    }
    try {
      const token = getToken();
      await axios.post(`${API}/auth/forgot-password`, { email: user.email }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('customerUsers.passwordResetSent', { email: user.email }));
    } catch (error) {
      toast.error(t('customerUsers.errorSendingPasswordReset'));
    }
  };

  const handleDeleteUser = async (userId) => {
    if (await confirm.danger(t('customerUsers.confirmDeleteUser'))) {
      try {
        const token = getToken();
        await axios.delete(`${API}/users/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(t('customerUsers.userDeleted'));
        fetchUsers();
      } catch (error) {
        // If user doesn't exist (404), still remove from the list
        if (error.response?.status === 404) {
          toast.info(t('customerUsers.userAlreadyDeleted'));
          fetchUsers();
        } else {
          toast.error(t('customerUsers.errorDeletingUser'));
        }
      }
    }
  };

  return (
    <CustomerLayout>
      <div className="admin-header">
        <h1>{t('customerUsers.title')}</h1>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <Plus size={20} /> {t('customerUsers.newUser')}
        </button>
      </div>

      <CollapsibleTable
        columns={[
          {
            key: 'name',
            label: t('customerUsers.name'),
            minWidth: '120px'
          },
          {
            key: 'email',
            label: t('customerUsers.email'),
            minWidth: '180px'
          },
          {
            key: 'last_login',
            label: 'Sist innlogget',
            minWidth: '140px',
            render: (user) => (
              <span style={{ color: user.last_login ? '#fff' : '#666', fontSize: '0.9rem' }}>
                {formatDateTime(user.last_login)}
              </span>
            )
          },
          {
            key: 'login_history',
            label: 'Logg',
            minWidth: '60px',
            render: (user) => (
              <button
                className="btn btn-secondary"
                style={{ 
                  padding: '0.4rem 0.6rem',
                  fontSize: '0.8rem'
                }}
                onClick={() => setShowLoginHistory(user)}
                title="Vis innloggingshistorikk"
              >
                <Clock size={16} />
              </button>
            )
          },
          {
            key: 'preferred_language',
            label: t('customerUsers.language'),
            minWidth: '90px',
            render: (user) => {
              const lang = availableLanguages.find(l => l.code === user.preferred_language);
              return lang ? lang.name : (user.preferred_language || '-');
            }
          }
        ]}
        data={users}
        actions={(user) => (
          <>
            <button 
              className="btn btn-primary" 
              style={{ fontSize: '0.8rem', padding: 'clamp(0.4rem, 1vw, 0.5rem) 0.75rem' }}
              onClick={() => handleEditUser(user)}
              title={t('customerUsers.edit')}
            >
              <Edit size={window.innerWidth < 1280 ? 12 : 14} />
            </button>
            <button 
              className="btn btn-secondary" 
              style={{ fontSize: '0.8rem', padding: 'clamp(0.4rem, 1vw, 0.5rem) 0.75rem' }}
              onClick={() => handleResetPassword(user)}
              title={t('customerUsers.sendPasswordReset')}
            >
              <Mail size={window.innerWidth < 1280 ? 12 : 14} />
            </button>
            <button 
              className="btn btn-danger" 
              style={{ fontSize: '0.8rem', padding: 'clamp(0.4rem, 1vw, 0.5rem) 0.75rem' }}
              onClick={() => handleDeleteUser(user.id)}
              title={t('customerUsers.delete')}
            >
              <Trash2 size={window.innerWidth < 1280 ? 12 : 14} />
            </button>
          </>
        )}
      />

      {/* Login History Popup */}
      {showLoginHistory && (
        <LoginHistoryPopup 
          user={showLoginHistory} 
          onClose={() => setShowLoginHistory(null)} 
        />
      )}

      {/* Create/Edit User Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => {
          setShowModal(false);
          setEditingUser(null);
          setFormData({ name: '', email: '', password: '', preferred_language: '' });
        }}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editingUser ? t('customerUsers.editUser') : t('customerUsers.newUser')}</h2>
            <form onSubmit={(e) => { e.preventDefault(); handleCreateUser(); }}>
              <div className="form-group">
                <label>{t('customerUsers.name')}</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder={t('customerUsers.namePlaceholder')}
                />
              </div>
              <div className="form-group">
                <label>{t('customerUsers.email')}</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>{editingUser ? t('customerUsers.newPassword') : t('customerUsers.password')}</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required={!editingUser}
                  placeholder={editingUser ? t('customerUsers.leaveBlankToKeep') : ''}
                />
              </div>
              <div className="form-group">
                <label>{t('customerUsers.language')}</label>
                <select
                  value={formData.preferred_language}
                  onChange={(e) => setFormData({...formData, preferred_language: e.target.value})}
                >
                  <option value="">{t('customerUsers.defaultLanguage')}</option>
                  {availableLanguages.map(lang => (
                    <option key={lang.code} value={lang.code}>{lang.name}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                <button type="submit" className="btn btn-primary">
                  {editingUser ? t('customerUsers.update') : t('customerUsers.create')}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => {
                  setShowModal(false);
                  setEditingUser(null);
                  setFormData({ name: '', email: '', password: '', preferred_language: '' });
                }}>{t('customerUsers.cancel')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </CustomerLayout>
  );
};

export default CustomerUsers;
